/**
 * TakePicker — §4.11's "keep N takes and let the user pick".
 *
 * Chatterbox is nondeterministic (verified: three identical requests, three different waveforms at
 * 5.24 / 5.16 / 5.04 s), which is why plain "Regenerate" needs no seed knob and why `take` is inside
 * `clipKey`. The consequence a picker exists for is the other half of that: a regeneration is a roll
 * of the dice, so the *second* one is sometimes worse than the first, and without a way back the only
 * recovery is to keep rolling and keep spending GPU.
 *
 * ⚠️ **Undo here costs ZERO GPU and about half a second.** `sentence_clips.active` is a pointer; the
 * clips all still exist on disk. Moving the pointer changes the page's ordered clip *content* shas,
 * which changes `pageArtifactKey`, which re-stitches the page on the **`media`** lane — 0.49 s
 * measured. So "put it back" is not a compromise: it is strictly cheaper and strictly faster than the
 * regeneration it undoes, and the UI says so rather than hedging.
 *
 * ── Auditioning ─────────────────────────────────────────────────────────────
 *
 * Each take plays from `GET /api/v1/sentences/:sid/takes/:take/audio?v=…`, which serves the clip's
 * FLAC. The `v` is the clip's content hash and is part of the ADDRESS, not an option — a pronunciation
 * fix changes `ttsText`, which changes `clipKey`, which points the same `(sid, take)` path at
 * different bytes. With `v` required, the URL is genuinely immutable; without it there would be no
 * version to check and no honest way to cache. It is **not** a blob address: LAW 18 — there is no
 * `GET /blob/:sha`, because a private book's clip and a public book's clip of the same sentence are
 * literally the same file, so a sha must never be a capability.
 *
 * ⚠️ The audition element is created here and is **not** the reader's `AudioEngine`. §5.7 keeps
 * exactly two `<audio>` elements alive and ping-pongs them so a page boundary has no audible gap; a
 * picker that borrowed one would leave the wrong element primed and the symptom is a book that plays
 * the same page twice with nothing in the console. A third element for a two-second clip costs
 * nothing and cannot corrupt that state. The reader is paused before an audition for the same reason
 * a phone does not play two things at once.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import { api, ApiError } from "../lib/api.ts";
import { injectReaderMenuCss } from "./SelectionMode.tsx";

// ─── the wire shapes ─────────────────────────────────────────────────────────

export interface TakeView {
  take: number;
  active: boolean;
  createdAt: number;
  createdBy: string | null;
  /** Null while the GPU is still working on it. LAW 9: absent is not zero, and it renders differently. */
  durationMs: number | null;
  host: string | null;
  /** First 16 hex of the clip's content sha, for the audition URL's `?v=`. Null until it exists. */
  v: string | null;
  params: { exaggeration: number | null; cfgWeight: number | null; temperature: number | null; variant: string } | null;
}

export interface TakesResponse {
  sid: string;
  editionId: string;
  bookId: string;
  voiceId: string;
  /** The prose as printed. */
  text: string;
  /** What is actually spoken — `text` after the §4.7 normaliser and any pronunciation override. */
  ttsText: string;
  /** `sentences.ttsText` before any override, so "Fix pronunciation…" can offer a Reset. */
  ttsTextDefault: string;
  speak: boolean;
  /** From `shared/access.ts`, decided on the server. LAW 13: the UI never infers this. */
  canRegenerate: boolean;
  /** Whether a per-sentence voice setting would survive the planner. Computed live, never a flag. */
  tuningApplies: boolean;
  params: { exaggeration: number; cfgWeight: number; temperature: number; variant: string };
  takes: TakeView[];
}

export interface RegenerateResponse {
  bookId: string;
  editionId: string;
  voiceId: string;
  sentences: { id: string; sid: string; takeAfter: number }[];
  jobs: { id: string; pageId: string; kind: string; lane: string; priority: number; status: string }[];
  revert: { sid: string; take: number | null }[];
}

/**
 * Every call this feature makes, in one object, so the paths appear exactly once each.
 *
 * It lives in this file rather than in `client/src/lib/api.ts` because that file is owned elsewhere
 * this phase; it is deliberately shaped like `abApi` so the move is a cut and paste when the two
 * meet. Every path carries `bookId` — a `sid` is only meaningful inside its edition (LAW 1), and the
 * server refuses a request that names neither rather than resolving it against whichever row the
 * database happened to return first.
 */
export const sentenceApi = {
  takes: (sid: string, bookId: string, voiceId?: string | null) =>
    api.get<TakesResponse>(`/sentences/${encodeURIComponent(sid)}/takes?bookId=${encodeURIComponent(bookId)}${voiceId ? `&voiceId=${encodeURIComponent(voiceId)}` : ""}`),

  regenerate: (
    sid: string,
    body: { bookId: string; reason: string; note?: string; with?: string[]; ttsTextOverride?: string; voiceId?: string | null },
  ) => api.post<RegenerateResponse>(`/sentences/${encodeURIComponent(sid)}/regenerate`, body),

  report: (sid: string, body: { bookId: string; reason: string; note?: string }) =>
    api.post<{ id: string; reported: boolean; message: string }>(`/sentences/${encodeURIComponent(sid)}/report`, body),

  activate: (sid: string, take: number, body: { bookId: string; voiceId?: string | null }) =>
    api.post<{ sid: string; take: number; pageId: string | null; job: { id: string; status: string } | null }>(
      `/sentences/${encodeURIComponent(sid)}/takes/${take}/activate`,
      body,
    ),

  speak: (sid: string, body: { bookId: string; speak: boolean; voiceId?: string | null }) =>
    api.post<{ sid: string; speak: boolean; pageId: string | null }>(`/sentences/${encodeURIComponent(sid)}/speak`, body),

  /** A URL, not a fetch: it goes on an `<audio src>`, which cannot carry a header (§6.1). */
  takeAudioUrl: (sid: string, take: number, v: string, bookId: string, voiceId?: string | null): string =>
    `/api/v1/sentences/${encodeURIComponent(sid)}/takes/${take}/audio?bookId=${encodeURIComponent(bookId)}&v=${encodeURIComponent(v)}${voiceId ? `&voiceId=${encodeURIComponent(voiceId)}` : ""}`,
};

// ─── the component ───────────────────────────────────────────────────────────

export interface TakePickerProps {
  sid: string;
  bookId: string;
  voiceId?: string | null;
  /** The reader pauses before an audition — a phone does not play two things at once. */
  onPauseReader?: (() => void) | null;
  onClose(): void;
  /** Fired after a take is made active, so the host can resync the page it is holding. */
  onActivated?: ((r: { sid: string; take: number; pageId: string | null }) => void) | null;
  onNotice?: ((message: string) => void) | null;
}

/**
 * How often the picker re-asks while a take is still generating.
 *
 * Two seconds, against a clip that costs 1.4–7.7 s of GPU (the upper figure measured on 2026-08-07,
 * when spark-1's card was shared with four other inference services). Faster is a poll storm for no
 * information; slower and the take the user is waiting for appears late enough to feel broken.
 */
const POLL_MS = 2_000;

export function TakePicker(props: TakePickerProps): ReactElement {
  const { sid, bookId, voiceId = null, onPauseReader, onClose, onActivated, onNotice } = props;

  const [state, setState] = useState<{ k: "loading" } | { k: "ready"; data: TakesResponse } | { k: "error"; message: string }>({ k: "loading" });
  const [busy, setBusy] = useState<number | null>(null);
  const [auditioning, setAuditioning] = useState<number | null>(null);

  /**
   * One `<audio>`, created once and reused. A new element per take would leak one per audition and,
   * on iOS, would spend the page's per-gesture unlock allowance on elements nobody hears again.
   */
  const audio = useRef<HTMLAudioElement | null>(null);
  if (audio.current === null && typeof Audio !== "undefined") audio.current = new Audio();

  const load = useCallback(async () => {
    try {
      const data = await sentenceApi.takes(sid, bookId, voiceId);
      setState({ k: "ready", data });
    } catch (err) {
      setState({ k: "error", message: err instanceof ApiError ? err.message : "Could not load the takes for this sentence." });
    }
  }, [sid, bookId, voiceId]);

  useEffect(() => {
    injectReaderMenuCss();
    void load();
  }, [load]);

  // Poll only while something is actually pending. A picker open on a settled sentence must not wake
  // a phone every two seconds for the rest of the night.
  const pending = state.k === "ready" && state.data.takes.some((t) => t.v === null);
  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(id);
  }, [pending, load]);

  useEffect(() => {
    const el = audio.current;
    return () => {
      el?.pause();
    };
  }, []);

  const audition = useCallback(
    (take: TakeView) => {
      const el = audio.current;
      if (el === null || take.v === null) return;
      if (auditioning === take.take) {
        el.pause();
        setAuditioning(null);
        return;
      }
      onPauseReader?.();
      el.pause();
      el.src = sentenceApi.takeAudioUrl(sid, take.take, take.v, bookId, voiceId);
      el.onended = () => setAuditioning(null);
      el.onerror = () => {
        setAuditioning(null);
        onNotice?.("That take could not be played — it may still be on its way from the storage machine.");
      };
      setAuditioning(take.take);
      void el.play().catch(() => {
        // Autoplay policy, or a clip that vanished under us. Neither is worth an exception; both are
        // worth saying out loud, because a silent no-op reads as "the button is broken".
        setAuditioning(null);
        onNotice?.("Tap once more to play — the browser wanted a gesture first.");
      });
    },
    [auditioning, sid, bookId, voiceId, onPauseReader, onNotice],
  );

  const activate = useCallback(
    async (take: number) => {
      setBusy(take);
      try {
        const r = await sentenceApi.activate(sid, take, { bookId, voiceId });
        onActivated?.({ sid: r.sid, take: r.take, pageId: r.pageId });
        onNotice?.(`Using take ${take}. The page is being restitched — about a second, and no GPU.`);
        await load();
      } catch (err) {
        onNotice?.(err instanceof ApiError ? err.message : "Could not switch takes.");
      } finally {
        setBusy(null);
      }
    },
    [sid, bookId, voiceId, onActivated, onNotice, load],
  );

  const body = useMemo(() => {
    if (state.k === "loading") return <p className="ab-sm-note">Loading takes…</p>;
    if (state.k === "error") return <p className="ab-sm-note ab-sm-bad">{state.message}</p>;

    const { data } = state;
    if (data.takes.length === 0) {
      return (
        <p className="ab-sm-note">
          This sentence has not been narrated in {data.voiceId} yet. Press play, or regenerate it, and the first take appears here.
        </p>
      );
    }

    return (
      <ul className="ab-sm-takes">
        {data.takes.map((t) => (
          <li key={t.take} className="ab-sm-take" data-active={t.active ? "1" : undefined}>
            <button
              type="button"
              className="ab-sm-take-play"
              disabled={t.v === null}
              aria-label={t.v === null ? `Take ${t.take} is still generating` : `${auditioning === t.take ? "Stop" : "Play"} take ${t.take}`}
              onClick={() => audition(t)}
            >
              {t.v === null ? "…" : auditioning === t.take ? "■" : "▶"}
            </button>
            <span className="ab-sm-take-name">
              Take {t.take}
              {t.active ? <span className="ab-sm-take-chip">in use</span> : null}
            </span>
            <span className="ab-sm-take-meta">{describeTake(t)}</span>
            {t.active || !data.canRegenerate ? null : (
              <button type="button" className="ab-sm-take-use" disabled={t.v === null || busy !== null} onClick={() => void activate(t.take)}>
                {busy === t.take ? "…" : "Use this"}
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  }, [state, auditioning, busy, audition, activate]);

  return (
    <div className="ab-sm-panel" role="dialog" aria-label="Takes of this sentence">
      <div className="ab-sm-head">
        <span className="ab-sm-title">Takes</span>
        <button type="button" className="ab-sm-x" onClick={onClose} aria-label="Close takes">
          ✕
        </button>
      </div>
      {state.k === "ready" ? <p className="ab-sm-quote">{state.data.text}</p> : null}
      {body}
      {state.k === "ready" && state.data.ttsText !== state.data.text ? (
        <p className="ab-sm-note">
          Spoken as: <span className="ab-sm-tts">{state.data.ttsText}</span>
        </p>
      ) : null}
      <p className="ab-sm-note ab-sm-quiet">Switching takes costs no GPU — the clips already exist and the page is restitched in about a second.</p>
    </div>
  );
}

/**
 * The one line under a take. Duration first, because it is the thing that differs audibly between two
 * takes of the same sentence and the thing a listener is actually comparing.
 */
function describeTake(t: TakeView): string {
  if (t.durationMs === null) return "generating…";
  const secs = (t.durationMs / 1000).toFixed(2);
  const when = new Date(t.createdAt);
  const stamp = Number.isFinite(when.getTime()) ? when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  return `${secs}s · ${stamp}`;
}

export default TakePicker;
